// The Users admin page: who can sign in to Hiring at this school, laid out
// as People's lists are (a header with the add action, the figures, then one
// table). A row opens that user's activity.
import React, { useState, useMemo } from 'react';
import { Button, EmptyState, Icon, Input, StatusPill, Table, useShellLayout } from '../ds';
import { PageHeader, StatRow, Stat, Section, Note } from '../components/page';
import UserForm from './UserForm';

const UsersTable = ({ users = [], onEdit, onDelete, onViewActivity, onRefresh }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const layout = useShellLayout();
  const mobile = Boolean(layout && layout.mobile);
  const searchLower = searchTerm.toLowerCase();

  // Filter users based on search term
  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      return (
        user.email?.toLowerCase().includes(searchLower) ||
        user.companyName?.toLowerCase().includes(searchLower)
      );
    });
  }, [users, searchTerm]);

  if (!Array.isArray(users)) {
    console.error('Users prop is not an array:', users);
    return (
      <Note tone="warning">
        Error loading users data. Please try again.
      </Note>
    );
  }

  const handleActivityClick = (user) => {
    console.log('Edit button clicked for user:', user); // Debugging log
    if (!user || !user._id) {
      console.error('Invalid user data:', user);
      return;
    }
    onViewActivity && onViewActivity(user);
  };

  const handleEditClick = (user) => {
    if (!user || !user._id) {
      console.error('Invalid user data for editing:', user);
      return;
    }
    onEdit && onEdit(user);
  };

  const handleDeleteClick = (userId) => {
    if (!userId) {
      console.error('Invalid user ID for deletion:', userId);
      return;
    }
    if (window.confirm('Are you sure you want to delete this user?')) {
      onDelete && onDelete(userId);
    }
  };

  // A button inside a row acts for itself, not as a click on the row.
  const own = (fn) => (e) => { e.stopPropagation(); fn(); };

  const active = users.filter((u) => u.status === 'active').length;
  const admins = users.filter((u) => u.role === 'admin').length;

  const columns = [
    { key: 'name', label: 'Name', render: (user) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span aria-hidden="true" style={{
          width: 36, height: 36, borderRadius: '50%', flex: 'none', display: 'grid', placeItems: 'center',
          background: 'var(--brand-primary-soft)', color: 'var(--brand-primary-ink)', fontWeight: 600,
        }}>
          {user.name?.[0]?.toUpperCase() || '?'}
        </span>
        <span style={{ fontWeight: 500, color: 'var(--text-1)' }}>{user.name}</span>
      </div>
    ) },
    { key: 'email', label: 'Email' },
    { key: 'companyName', label: 'Company' },
    { key: 'role', label: 'Role', render: (user) => (
      <StatusPill tone={user.role === 'admin' ? 'info' : 'neutral'} dot={false}>{user.role || 'user'}</StatusPill>
    ) },
    { key: 'status', label: 'Status', render: (user) => (
      <StatusPill tone={user.status === 'active' ? 'success' : 'danger'}>{user.status || 'inactive'}</StatusPill>
    ) },
    { key: 'actions', label: 'Actions', render: (user) => (
      <div style={{ display: 'flex', gap: 4 }}>
        <Button size="sm" variant="ghost" icon={<Icon name="chart-bar" />} onClick={own(() => handleActivityClick(user))}>View Activity</Button>
        <Button size="sm" variant="ghost" onClick={own(() => handleEditClick(user))}>Edit</Button>
        <Button size="sm" variant="ghost" icon={<Icon name="trash" />} onClick={own(() => handleDeleteClick(user._id))}>Delete</Button>
      </div>
    ) },
  ];

  return (
    <>
      <PageHeader
        title="Users"
        subtitle="Who can sign in to Hiring here. Open a user to see what they have done."
        actions={<UserForm onSuccess={onRefresh} />}
      />

      <StatRow>
        <Stat label="Users" value={users.length} hint="everyone with a login" />
        <Stat label="Active" value={active} hint="can sign in now" />
        <Stat label="Not active" value={users.length - active} hint="cannot sign in" tone={users.length - active ? 'warning' : undefined} />
        <Stat label="Admins" value={admins} hint="can open these admin pages" />
      </StatRow>

      <Section
        title="All users"
        subtitle={`${filteredUsers.length} shown`}
        padding="20px 0 8px"
        action={(
          <div style={{ width: mobile ? 180 : 320 }}>
            <Input
              placeholder="Search users by name, email, or company..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              icon={<Icon name="magnifying-glass" />}
            />
          </div>
        )}
      >
        {filteredUsers.length === 0 ? (
          <EmptyState
            icon={<Icon name="users-three" size={36} />}
            title={searchTerm ? 'No matches' : 'No users yet'}
            description={searchTerm
              ? 'No users found matching your search criteria.'
              : 'No users found. Add a new user to get started.'}
          />
        ) : (
          <Table
            columns={columns}
            rows={filteredUsers}
            getRowId={(u) => u._id}
            onRowClick={handleActivityClick}
            stacked={mobile}
          />
        )}
      </Section>
    </>
  );
};

export default UsersTable;
